/* eslint-disable no-underscore-dangle */
/**
 * "Rögzített költségek" — what the driver has already submitted, newest first,
 * with where each one stands (Beküldve / Elfogadva / Elutasítva).
 *
 * The amounts are the driver's own typed-in claims; nothing here is a price.
 *
 * A claim tied to a trip names it ("Úthoz: PU_DASH_…") when the trip is one
 * the day screen has loaded (`trips`); the list view carries only the
 * booking id, so an older trip reads "Úthoz kötve". The API keeps no review
 * note, so a rejection shows its status and nothing more.
 */
import React from 'react';
import { EXPENSE_COPY } from '../portalFormat';
import { ReceiptIcon } from './Icons';

// Enough to answer "did my parking ticket go through?" without turning the
// day screen into an expense report.
const EXPENSES_SHOWN = 8;

const newestFirst = (rows) => [...rows].sort(
  (a, b) => new Date(b.created_at) - new Date(a.created_at),
);

/**
 * `copy` holds the words, the status and date reading and the amount format:
 * the driver's Hungarian by default (EXPENSE_COPY), English on the operator
 * screens.
 */
export default function ExpenseList({
  expenses, error = '', now, trips = [], copy = EXPENSE_COPY,
}) {
  if (error) {
    return (
      <>
        <div className="pp-section-head">
          <span className="pp-section-head__title">{copy.listTitle}</span>
        </div>
        <div className="pp-stub-note pp-expenses__error">{error}</div>
      </>
    );
  }

  if (!expenses?.length) return null;

  const rows = newestFirst(expenses);
  const shown = rows.slice(0, EXPENSES_SHOWN);

  return (
    <>
      <div className="pp-section-head">
        <span className="pp-section-head__title">{copy.listTitle}</span>
        <span className="pp-section-head__count">{copy.count(rows.length)}</span>
      </div>
      <ul className="pp-expenses" aria-label={copy.listTitle}>
        {shown.map((expense) => {
          const status = copy.status(expense.status);
          const meta = [
            copy.when(expense.created_at, now),
            copy.tripText(expense, trips),
            expense.note,
          ].filter(Boolean).join(' · ');
          return (
            <li className="pp-expense" key={expense._id}>
              <span className="pp-expense__icon" aria-hidden="true">
                <ReceiptIcon size={18} />
              </span>
              <span className="pp-expense__body">
                <span className="pp-expense__title">
                  {copy.typeName(expense.type)}
                  <span className="pp-expense__amount">
                    {copy.amount(expense.amount, expense.currency)}
                  </span>
                </span>
                {meta ? <span className="pp-expense__meta">{meta}</span> : null}
              </span>
              <span className={`pp-badge pp-badge--${status.tone}`}>{status.label}</span>
            </li>
          );
        })}
      </ul>
      {rows.length > shown.length ? (
        <div className="pp-stub-note">
          {copy.more(rows.length - shown.length)}
        </div>
      ) : null}
    </>
  );
}
